import { Link as LinkIcon } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import React, { useState } from "react";
import LinkedDOsModal from "../AppModals/LinkedDoModal";
interface TableProps {
  columns: string[];
  data: Array<Record<string, any>>;
  viewState: number | undefined;
  setViewState: React.Dispatch<React.SetStateAction<number | undefined>>;
  returned: number | null;
  setReturned: React.Dispatch<React.SetStateAction<number | null>>;
  setOrderState: React.Dispatch<React.SetStateAction<string | null>>;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  totalPages: number;
  search?: string;
  statusFilter?: string;
}

const DeliveryOrderTableComponent: React.FC<TableProps> = ({
  columns,
  data,
  viewState,
  setViewState,
  returned,
  setReturned,
  setOrderState,
  currentPage,
  setCurrentPage,
  totalPages,
  search,
  statusFilter,
}) => {
  // Add state for modal
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [selectedOrderReference, setSelectedOrderReference] =
    useState<string>("");

  const handleView = (rowId: number) => {
    setViewState(rowId);
  };

  const handleLinkedDOsClick = (orderId: number, referenceCode: string) => {
    setSelectedOrderId(orderId);
    setSelectedOrderReference(referenceCode);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedOrderId(null);
    setSelectedOrderReference("");
  };

  const getStatusBadge = (
    status: string,
    displayName: string
  ): { className: string; label: string } => {
    const statusMap: Record<string, { className: string; label: string }> = {
      DRAFT: {
        className: "bg-gray-100 text-gray-800 border border-gray-300",
        label: displayName || "Draft",
      },
      CREATED: {
        className: "bg-blue-100 text-blue-800 border border-blue-300",
        label: displayName || "Created",
      },
      COMPLETED: {
        className: "bg-green-100 text-green-800 border border-green-300",
        label: displayName || "Completed",
      },
      IN_TRANSIT: {
        className: "bg-yellow-100 text-yellow-800 border border-yellow-300",
        label: displayName || "In Transit",
      },
      DELIVERED: {
        className: "bg-green-100 text-green-800 border border-green-300",
        label: displayName || "Delivered",
      },
      CANCELLED: {
        className: "bg-red-100 text-red-800 border border-red-300",
        label: displayName || "Cancelled",
      },
    };
    return (
      statusMap[status] || {
        className: "bg-gray-100 text-gray-800 border border-gray-300",
        label: displayName || "Unknown",
      }
    );
  };

  const getActionButton = (row: any) => {
    if (row["Status"].name === "COMPLETED") {
      return (
        <button
          onClick={() => handleView(row.ID)}
          className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 transition-colors">
          View
        </button>
      );
    } else {
      return (
        <button
          onClick={() => {
            setReturned(row.ID);
            setOrderState(row.Status.name);
          }}
          className="px-3 py-1.5 bg-red-600 text-white rounded-md text-xs font-medium hover:bg-red-700 transition-colors">
          Edit
        </button>
      );
    }
  };

  // Updated table columns to include Linked DO's
  const tableColumns = [
    { key: "#", label: "#", width: "w-16" },
    { key: "ID", label: "ID", width: "w-24" },
    { key: "Reference", label: "Reference", width: "w-32" },
    { key: "From", label: "From", width: "w-48" },
    { key: "To", label: "To", width: "w-48" },
    { key: "Creation Date", label: "Creation Date", width: "w-40" },
    { key: "Total", label: "Total Cost", width: "w-32" },
    { key: "Status", label: "Status", width: "w-32" },
    { key: "Linked DOs", label: "Linked DO's", width: "w-24" }, // New column
    { key: "Actions", label: "Actions", width: "w-24" },
  ];

  const getRowNumber = (index: number) => {
    return currentPage * 10 + index + 1;
  };

  return (
    <div className="w-full">
      {/* Table */}
      <div className="overflow-hidden">
        <table className="min-w-full bg-white">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {tableColumns.map((column) => (
                <th
                  key={column.key}
                  className={`${column.width} px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data.length > 0 ? (
              data.map((row, index) => {
                const statusInfo = getStatusBadge(
                  row["Status"].name,
                  row["Status"].displayName
                );
                const rowNumber = getRowNumber(index);
                const isHighlighted = row["Status"].name === "COMPLETED";

                return (
                  <tr
                    key={row.ID}
                    className={`hover:bg-gray-50 transition-colors ${
                      isHighlighted
                        ? "bg-blue-50 border-l-4 border-l-blue-500"
                        : ""
                    }`}>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {rowNumber}
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900">
                      {row.ID}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {row.Reference || "N/A"}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {row.From || "N/A"}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {row.To || "N/A"}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {row["Creation Date"]}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {row.Total ?? "N/A"}
                    </td>
                    <td className="px-4 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.className}`}>
                        {statusInfo.label}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <Tooltip title="View Linked Orders">
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleLinkedDOsClick(row.ID, row.Reference)
                          }
                          sx={{
                            color: "primary.main",
                            "&:hover": {
                              backgroundColor: "primary.light",
                              color: "primary.dark",
                            },
                          }}>
                          <LinkIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </td>
                    <td className="px-4 py-4">{getActionButton(row)}</td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={tableColumns.length}
                  className="px-4 py-12 text-center text-gray-500">
                  <div className="flex flex-col items-center">
                    <svg
                      className="w-12 h-12 text-gray-300 mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2 2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                      />
                    </svg>
                    <p className="text-lg font-medium text-gray-900 mb-1">
                      No delivery orders found
                    </p>
                    <p className="text-gray-500">
                      {search && statusFilter
                        ? `No orders match "${search}" with status "${statusFilter}"`
                        : search
                        ? `No orders match "${search}"`
                        : statusFilter
                        ? `No orders found with status "${statusFilter}"`
                        : "Try adjusting your search or filter criteria"}
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(0)}
              disabled={currentPage === 0}
              className={`px-3 py-2 text-xs font-medium rounded-md border ${
                currentPage === 0
                  ? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50 bg-white"
              }`}>
              First
            </button>

            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
              disabled={currentPage === 0}
              className={`p-2 rounded-md border ${
                currentPage === 0
                  ? "border-gray-200 text-gray-400 cursor-not-allowed"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}>
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))
              }
              disabled={currentPage === totalPages - 1}
              className={`p-2 rounded-md border ${
                currentPage === totalPages - 1
                  ? "border-gray-200 text-gray-400 cursor-not-allowed"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}>
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>

            <button
              onClick={() => setCurrentPage(totalPages - 1)}
              disabled={currentPage === totalPages - 1}
              className={`px-3 py-2 text-xs font-medium rounded-md border ${
                currentPage === totalPages - 1
                  ? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50 bg-white"
              }`}>
              Last
            </button>
          </div>

          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-700">
              Page {currentPage + 1} of {totalPages}
            </span>
          </div>
        </div>
      )}

      {/* Linked DOs Modal */}
      <LinkedDOsModal
        open={modalOpen}
        onClose={handleCloseModal}
        deliveryOrderId={selectedOrderId}
        referenceCode={selectedOrderReference}
      />
    </div>
  );
};

export default DeliveryOrderTableComponent;
